import { useState } from 'react';
import { Copy, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import type { CopyBudgetSections, CopyTargetVenue } from './types';

interface CopyBudgetPanelProps {
  sourceVenueName: string;
  sourceCurrency: string;
  targetVenues: CopyTargetVenue[];
  onCopy: (targetShortlistIds: string[], sections: CopyBudgetSections) => void;
  onClose: () => void;
}

const DEFAULT_SECTIONS: CopyBudgetSections = {
  meals: true,
  meetings: true,
  activities: true,
  otherCosts: true,
  roomBreakdown: false,
  budgetNote: true,
};

export function CopyBudgetPanel({
  sourceVenueName,
  sourceCurrency,
  targetVenues,
  onCopy,
  onClose,
}: CopyBudgetPanelProps) {
  const [sections, setSections] = useState<CopyBudgetSections>(DEFAULT_SECTIONS);
  const [selectedTargets, setSelectedTargets] = useState<Set<string>>(
    () => new Set(targetVenues.map((v) => v.shortlistId))
  );

  const toggleSection = (key: keyof CopyBudgetSections) => {
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTarget = (shortlistId: string) => {
    setSelectedTargets((prev) => {
      const next = new Set(prev);
      if (next.has(shortlistId)) next.delete(shortlistId);
      else next.add(shortlistId);
      return next;
    });
  };

  const anySectionSelected = Object.values(sections).some(Boolean);
  const anyTargetSelected = selectedTargets.size > 0;
  const canCopy = anySectionSelected && anyTargetSelected;

  const handleCopy = () => {
    if (!canCopy) return;
    onCopy(Array.from(selectedTargets), sections);
  };

  const hasCurrencyMismatch = targetVenues.some(
    (v) => selectedTargets.has(v.shortlistId) && v.venueCurrency !== sourceCurrency
  );

  const sectionOptions: { key: keyof CopyBudgetSections; label: string; hint?: string }[] = [
    { key: 'meals', label: 'Meals' },
    { key: 'meetings', label: 'Meetings' },
    { key: 'activities', label: 'Activities' },
    { key: 'otherCosts', label: 'Other Costs (flights, ground transport, misc items)' },
    { key: 'budgetNote', label: 'Budget Note' },
    { key: 'roomBreakdown', label: 'Room Breakdown', hint: 'rates are hotel-specific' },
  ];

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Copy className="w-4 h-4 text-blue-600" />
          <h4 className="font-semibold text-blue-900">
            Copy Budget from {sourceVenueName}
          </h4>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-blue-100 rounded transition-colors"
        >
          <X className="w-4 h-4 text-blue-600" />
        </button>
      </div>

      {/* Sections */}
      <div>
        <p className="text-sm font-medium text-blue-800 mb-2">Sections to copy:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {sectionOptions.map(({ key, label, hint }) => (
            <label
              key={key}
              className="flex items-start gap-2 cursor-pointer text-sm text-blue-900 hover:bg-blue-100 rounded px-2 py-1.5 transition-colors"
            >
              <input
                type="checkbox"
                checked={sections[key]}
                onChange={() => toggleSection(key)}
                className="mt-0.5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
              />
              <span>
                {label}
                {hint && (
                  <span className="text-xs text-blue-500 ml-1">({hint})</span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Target venues */}
      <div>
        <p className="text-sm font-medium text-blue-800 mb-2">Copy to:</p>
        <div className="space-y-1">
          {targetVenues.map((venue) => (
            <label
              key={venue.shortlistId}
              className="flex items-center gap-2 cursor-pointer text-sm text-blue-900 hover:bg-blue-100 rounded px-2 py-1.5 transition-colors"
            >
              <input
                type="checkbox"
                checked={selectedTargets.has(venue.shortlistId)}
                onChange={() => toggleTarget(venue.shortlistId)}
                className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
              />
              <span>{venue.venueName}</span>
              {venue.venueCurrency !== sourceCurrency && selectedTargets.has(venue.shortlistId) && (
                <span className="text-xs text-amber-600 ml-1">
                  (uses {venue.venueCurrency} — values copied as-is)
                </span>
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-100 rounded px-3 py-2">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Existing data in the selected sections will be overwritten. You can review changes on each venue tab before saving.
          {hasCurrencyMismatch && ' Numeric values are copied without currency conversion.'}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          size="sm"
          onClick={handleCopy}
          disabled={!canCopy}
          leftIcon={<Copy className="w-4 h-4" />}
        >
          Copy Budget to {selectedTargets.size} Venue{selectedTargets.size !== 1 ? 's' : ''}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
